import { useMemo } from 'react';
import { Outlet, useParams } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';

import { NetworkStatusScopeProvider } from 'components/groupPayerNetworkStatuses/networkStatusScope';
import CircularProgress from 'components/shared/CircularProgress';
import { useProfile } from 'context/profile';
import { PERMISSIONS } from 'models/Profile';
import { adminClientsQueryKeys, adminShowClient } from 'services/api/admin/clients';

import { ADMIN_NETWORK_STATUS_PATH } from './payerNetworkStatusTab';

// The customer's Facility Network Status screens pointed at a chosen client. No inbound referrals check,
// unlike the customer route: Olio staff set a client up before it goes live.
export default function AdminPayerNetworkStatus() {
  const { id } = useParams<{ id: string }>();
  const { profile } = useProfile();
  const canEdit = profile.has(PERMISSIONS.adminGroupPayerNetworkStatusEdit);

  const { data: client, isLoading } = useQuery({
    queryKey: adminClientsQueryKeys.show(id!),
    queryFn: ({ signal }) => adminShowClient(id!, signal),
    enabled: !!id,
  });

  const scope = useMemo(
    () =>
      client && {
        clientId: id,
        clientName: client.name,
        basePath: `${ADMIN_NETWORK_STATUS_PATH}/${id}`,
        canEdit,
        backPath: ADMIN_NETWORK_STATUS_PATH,
      },
    [client, id, canEdit]
  );

  // A client outside the admin's scope 404s here too, so this stops at the spinner instead of showing
  // an empty page with a live Add button.
  if (isLoading || !scope) return <CircularProgress />;

  return (
    <NetworkStatusScopeProvider value={scope}>
      <Outlet />
    </NetworkStatusScopeProvider>
  );
}
